type AnalyticsValue = string | number | boolean | string[]
type DataLayerItem = Record<string, AnalyticsValue> | IArguments
type YandexMetricaFunction = ((...args: unknown[]) => void) & {
  a?: IArguments[]
  l?: number
}

declare global {
  interface Window {
    dataLayer?: DataLayerItem[]
    ym?: YandexMetricaFunction
  }
}

const gtmId = import.meta.env.VITE_GTM_ID?.trim() || 'GTM-NX9CQ486'
const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID?.trim() || 'AW-18338716662'
const yandexMetricaId = Number(import.meta.env.VITE_YANDEX_METRIKA_ID?.trim() || '110944981')
const waitlistConversionDestination = import.meta.env.VITE_GOOGLE_ADS_WAITLIST_DESTINATION?.trim()
  || 'AW-18338716662/eUNNCPiFs9QcEPa3yqhE'
let analyticsScheduled = false
let yandexMetricaInitialized = false
let lastTrackedUrl = typeof window === 'undefined' ? '' : window.location.href

function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(arguments)
}

function injectGtm() {
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`
  document.head.appendChild(script)
}

function ensureYandexMetrica() {
  if (window.ym) return window.ym
  const queue = function (..._args: unknown[]) {
    queue.a = queue.a || []
    queue.a.push(arguments)
  } as YandexMetricaFunction
  queue.l = Date.now()
  window.ym = queue
  return queue
}

function initializeYandexMetrica() {
  if (!Number.isInteger(yandexMetricaId) || yandexMetricaId <= 0 || yandexMetricaInitialized) return
  yandexMetricaInitialized = true
  ensureYandexMetrica()(yandexMetricaId, 'init', {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true,
  })
}

function injectYandexMetrica() {
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://mc.yandex.ru/metrika/tag.js'
  document.head.appendChild(script)
}

function injectAnalytics() {
  injectGtm()
  if (yandexMetricaInitialized) injectYandexMetrica()
}

function scheduleAnalytics() {
  const requestIdle = (window as Window & {
    requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number
  }).requestIdleCallback
  if (requestIdle) {
    requestIdle(injectAnalytics, { timeout: 2000 })
  } else {
    globalThis.setTimeout(injectAnalytics, 1)
  }
}

export function initializeAnalytics() {
  window.dataLayer = window.dataLayer || []

  if (
    !gtmId || !/^GTM-[A-Z0-9]+$/.test(gtmId)
    || !googleAdsId || !/^AW-[0-9]+$/.test(googleAdsId)
    || analyticsScheduled
  ) return
  analyticsScheduled = true

  window.dataLayer.push({ event: 'gtm.js', 'gtm.start': Date.now() })
  gtag('js', new Date())
  gtag('config', googleAdsId, { allow_ad_personalization_signals: false })
  initializeYandexMetrica()
  if (document.readyState === 'complete') scheduleAnalytics()
  else window.addEventListener('load', scheduleAnalytics, { once: true })
}

export function trackEvent(event: string, parameters: Record<string, AnalyticsValue> = {}) {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event, ...parameters })
  if (event === 'sidewisp_waitlist_joined' && yandexMetricaInitialized) {
    window.ym?.(yandexMetricaId, 'reachGoal', 'sidewisp_waitlist_joined')
  }
}

export function trackPageView(url = window.location.href) {
  if (!yandexMetricaInitialized || url === lastTrackedUrl) return
  window.ym?.(yandexMetricaId, 'hit', url, {
    title: document.title,
    referer: lastTrackedUrl,
  })
  lastTrackedUrl = url
}

export function trackWaitlistConversion() {
  gtag('event', 'conversion', {
    send_to: waitlistConversionDestination,
    value: 1,
    currency: 'USD',
  })
}
